import type { User } from 'src/types/user'
import { isInternalUser } from 'src/utils/functions'

interface VitallySDK {
  init: (token: string, apiUrl: string) => void
  user: (user: { userId: string; accountId: string; traits: any }) => void
  account: (account: { accountId: string; traits: any }) => void
  organization: (org: any) => void
  track: (event: { event: string; userId: string; properties?: any }) => void
  nps: (
    type: 'survey' | 'show' | 'hide',
    options: {
      productName: string
      delay?: number
    }
  ) => void
}

declare global {
  interface Window {
    Vitally: VitallySDK
  }
}
// Use imported User type from 'src/types/user'

class VitallyManager {
  private static instance: VitallyManager
  private isEnabled = false
  private userId: string | null = null
  private userEmail: string | null = null
  private userName: string | null = null
  private org: string | null = null
  private userCreatedAt: Date | null = null

  private constructor() {}

  static getInstance(): VitallyManager {
    if (!VitallyManager.instance) {
      VitallyManager.instance = new VitallyManager()
    }
    return VitallyManager.instance
  }

  async initialize(user: User | null, magicLinkFromAdmin: boolean): Promise<void> {
    // Single decision point: Should we enable Vitally?
    this.isEnabled = !!(
      user &&
      !magicLinkFromAdmin &&
      !isInternalUser(user) &&
      user.org // Only track users with an organization
    )

    if (!this.isEnabled) {
      return
    }

    // Store user information
    this.userId = user.uid
    this.userEmail = user.email || null
    this.userName = user.displayName || null
    this.org = user.org || null
    this.userCreatedAt = new Date(Number(user?.metadata?.createdAt)) || null

    // SDK is already initialized in index.html, just track user
    if (window.Vitally) {
      // Track initial user and account
      this.trackUser()
      this.trackSessionStart()

      // Track login event
      this.track('Login', {
        email: this.userEmail,
        timestamp: new Date().toISOString()
      })

      this.triggerNPS()
    } else {
      this.isEnabled = false
    }
  }

  private trackUser(): void {
    if (!this.isEnabled || !this.userId || !this.org || !window.Vitally) return

    try {
      // Track account first
      window.Vitally.account({
        accountId: this.org,
        traits: {
          name: this.org
        }
      })

      // Track user
      window.Vitally.user({
        userId: this.userId,
        accountId: this.org,
        traits: {
          email: this.userEmail,
          name: this.userName,
          createdAt: this.userCreatedAt
        }
      })
    } catch (error) {
      throw error
    }
  }

  private trackSessionStart(): void {
    this.track('Session Started', {
      timestamp: new Date().toISOString()
    })
  }

  track(eventName: string, properties?: Record<string, any>): void {
    if (!this.isEnabled || !this.userId || !window.Vitally) {
      return
    }

    try {
      const payload = {
        event: eventName,
        userId: this.userId,
        properties: {
          ...properties,
          org: this.org
        }
      }

      window.Vitally.track(payload)

      // Update user traits for certain events
      if (properties?.page || properties?.feature || properties?.module) {
        window.Vitally.user({
          userId: this.userId,
          accountId: this.org!,
          traits: {
            lastSeen: new Date().toISOString(),
            lastActivity: eventName,
            lastModule: properties.page || properties.feature || properties.module,
            createdAt: this.userCreatedAt
          }
        })
      }
    } catch (error) {
      throw error
    }
  }

  trackPageView(page: string, path: string): void {
    this.track('Page Viewed', {
      page,
      path,
      timestamp: new Date().toISOString()
    })
  }

  trackFeatureUsed(feature: string, metadata?: Record<string, any>): void {
    this.track('Feature Used', {
      feature,
      timestamp: new Date().toISOString(),
      ...metadata
    })
  }

  triggerNPS(delay: number = 5000): void {
    if (!this.isEnabled || !this.userId || !window.Vitally) return

    try {
      window.Vitally.nps('survey', {
        productName: 'Loop Frontend',
        delay
      })
    } catch (error) {
      throw error
    }
  }
}

// Export singleton instance
export const vitallyManager = VitallyManager.getInstance()
